import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const pageWindow = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);

  const numbers = new Set([1, pages, page, page - 1, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((value) => numbers.add(value));
  if (page >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach((value) => numbers.add(value));

  const sorted = [...numbers].filter((value) => value >= 1 && value <= pages).sort((a, b) => a - b);

  return sorted.reduce((list, value, index) => {
    if (index && value - sorted[index - 1] > 1) list.push('gap');
    list.push(value);
    return list;
  }, []);
};

export const Pagination = ({ page, pages, total, limit, onPage }) => {
  const { t } = useTranslation();
  const safePages = Math.max(pages || 1, 1);
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, total || 0);

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-beam-blue/10 px-4 py-4 sm:flex-row">
      <p className="text-sm font-bold text-beam-graphite/60 tabular-nums">
        {t('admin.showingRange', { from, to, total: total || 0 })}
      </p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="focus-ring inline-flex h-9 items-center gap-1 rounded-lg border border-beam-blue/10 px-2.5 text-sm font-bold text-beam-ink hover:bg-beam-light disabled:cursor-not-allowed disabled:opacity-45"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t('common.previous')}</span>
        </button>

        {pageWindow(page, safePages).map((value, index) =>
          value === 'gap' ? (
            <span key={`gap-${index}`} className="px-1 text-sm font-bold text-beam-graphite/40">
              &hellip;
            </span>
          ) : (
            <button
              key={value}
              type="button"
              onClick={() => onPage(value)}
              aria-current={value === page ? 'page' : undefined}
              className={`focus-ring h-9 min-w-9 rounded-lg px-2 text-sm font-extrabold tabular-nums transition ${
                value === page
                  ? 'bg-beam-blue text-white'
                  : 'border border-beam-blue/10 text-beam-graphite hover:bg-beam-light'
              }`}
            >
              {value}
            </button>
          ),
        )}

        <button
          type="button"
          disabled={page >= safePages}
          onClick={() => onPage(page + 1)}
          className="focus-ring inline-flex h-9 items-center gap-1 rounded-lg border border-beam-blue/10 px-2.5 text-sm font-bold text-beam-ink hover:bg-beam-light disabled:cursor-not-allowed disabled:opacity-45"
        >
          <span className="hidden sm:inline">{t('common.next')}</span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
